import React, { useEffect, useState } from 'react'
import './ExperimentationGroups.css'
import { Icon, Spinner, SpinnerSize, Stack, Text } from 'office-ui-fabric-react'
import { ScreenHeader } from '../../Components/ScreenHeader'
import { DataValue, graphql } from 'react-apollo'
import { ExperimentationGroupsTable } from './ExperimentationGroupsTable'
import {
  createExperimentationGroup,
  deleteExperimentationGroup,
  editExperimentationGroup,
  ExperimentationGroup,
  ExperimentationGroupsAndAccountsQueryResponse,
  getExperimentationGroupsAndAccounts,
  getExperimentationGroupsAndAccountsQuery,
} from '../../Api/experimentationGroups'
import { CreateOrManageExperimentationGroupDialog } from './CreateOrManageExperimentationGroupDialog'
import { Account } from '../../Api/integrations'
import { ConfirmationDialog } from '../../Components/ConfirmationDialog'

interface Dialog {
  type: 'create' | 'manage' | 'delete'
  experimentationGroup?: ExperimentationGroup
}

function ExperimentationGroupsScreen({
  data,
}: {
  data?: DataValue<ExperimentationGroupsAndAccountsQueryResponse, {}>
}) {
  const isDataReady =
    data && !data.loading && !!data.experimentGroups && !!data.allAccounts
  const [dialog, setDialog] = useState<Dialog | null>(null)
  useEffect(() => {
    if (!isDataReady) {
      return
    }
    const pathMatch = window.location.hash.match(/\/#\/manage\/([^/]+)$/)
    if (!pathMatch) {
      return
    }
    const experimentationGroupId = pathMatch[1]
    const experimentationGroup = data!.experimentGroups!.find(
      ({ id }) => id === experimentationGroupId
    )
    setDialog({ type: 'manage', experimentationGroup })
  }, [setDialog, data, isDataReady])
  let { experimentGroups: experimentationGroups, allAccounts } = data!
  experimentationGroups = experimentationGroups!
  allAccounts = allAccounts!
  return (
    <>
      <Stack
        className="screen"
        horizontalAlign="start"
        verticalAlign="start"
        verticalFill
        style={{ width: '100%' }}
      >
        <ScreenHeader
          path={['Experimentation Groups']}
          buttons={[
            {
              name: 'Create Experimentation Group',
              icon: 'Add',
              disabled: !isDataReady,
              onClick: () => {
                setDialog({ type: 'create' })
              },
            },
          ]}
        />
        <Stack
          className="experimentation-groups-screen"
          horizontalAlign="center"
          verticalAlign="start"
          horizontal
          wrap
          styles={{
            root: {
              width: '100%',
              overflow: 'auto',
            },
            inner: {
              padding: 60,
            },
          }}
        >
          {!isDataReady ? (
            <Stack
              className="layout"
              horizontalAlign="center"
              verticalAlign="center"
              verticalFill
            >
              <Spinner size={SpinnerSize.large} label="Loading" />
            </Stack>
          ) : experimentationGroups.length ? (
            <ExperimentationGroupsTable
              experimentationGroups={experimentationGroups}
              onDeleteClick={(experimentationGroup) => {
                setDialog({ type: 'delete', experimentationGroup })
                removeExperimentationGroupFromHash()
              }}
              onManageClick={(experimentationGroup) => {
                setDialog({ type: 'manage', experimentationGroup })
                removeExperimentationGroupFromHash()
                addExperimentationGroupToHash(experimentationGroup.id)
              }}
            />
          ) : (
            <Empty />
          )}
          {dialog && (dialog.type === 'create' || dialog.type === 'manage') ? (
            <CreateOrManageExperimentationGroupStatefulDialog
              experimentationGroup={dialog.experimentationGroup}
              accounts={allAccounts}
              onClose={() => {
                removeExperimentationGroupFromHash()
                setDialog(null)
              }}
            />
          ) : null}
          {dialog && dialog.type === 'delete' ? (
            <ConfirmationStatefulDialog
              experimentationGroup={dialog.experimentationGroup!}
              onClose={() => {
                setDialog(null)
              }}
            />
          ) : null}
        </Stack>
      </Stack>
    </>
  )
}

function Empty() {
  return (
    <Stack
      horizontalAlign="center"
      verticalAlign="center"
      verticalFill
      style={{ width: '100%' }}
    >
      <Text
        variant="xLarge"
        style={{
          maxWidth: 600,
          padding: 20,
          textAlign: 'center',
        }}
      >
        There are no experimentation groups configured yet. You can add an
        experimentation group by pressing the
        <i>Create Experimentation Group</i>{' '}
        <Icon iconName="Add" style={{ verticalAlign: 'middle' }} />
        &nbsp;button.
      </Text>
    </Stack>
  )
}

function CreateOrManageExperimentationGroupStatefulDialog({
  experimentationGroup,
  accounts,
  onClose,
}: {
  experimentationGroup?: ExperimentationGroup
  accounts: Account[]
  onClose: () => void
}) {
  const [disabled, setDisabled] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  return (
    <CreateOrManageExperimentationGroupDialog
      experimentationGroup={experimentationGroup}
      accounts={accounts}
      onSave={async (experimentationGroup) => {
        setDisabled(true)
        try {
          if (experimentationGroup.id) {
            await editExperimentationGroup(experimentationGroup)
          } else {
            await createExperimentationGroup(experimentationGroup)
          }
          await getExperimentationGroupsAndAccounts()
          onClose()
          return
        } catch (error) {
          setError((error as any).message)
        }
        setDisabled(false)
      }}
      onCancel={onClose}
      error={error}
      disabled={disabled}
    />
  )
}

function ConfirmationStatefulDialog({
  experimentationGroup,
  onClose,
}: {
  experimentationGroup: ExperimentationGroup
  onClose: () => void
}) {
  const [error, setError] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  return (
    <ConfirmationDialog
      dialogTitle={`Delete ${experimentationGroup.title}`}
      confirmationText={`Are you sure you want to delete the experimentation group "${experimentationGroup.title}"?`}
      errorMessage={
        error
          ? 'Could not delete placement. If the problem persist, please contact support.'
          : undefined
      }
      yesButtonText="Yes"
      noButtonText="No"
      isLoading={isSubmitting}
      onCancel={onClose}
      onConfirm={async () => {
        try {
          setIsSubmitting(true)
          await deleteExperimentationGroup(experimentationGroup.id)
          await getExperimentationGroupsAndAccounts()
          onClose()
          return
        } catch (error) {
          setError(true)
        }
        setIsSubmitting(false)
      }}
    />
  )
}

const withGql = graphql<{}, ExperimentationGroupsAndAccountsQueryResponse>(
  getExperimentationGroupsAndAccountsQuery,
  {
    options: () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }),
  }
)
const ExperimentationGroupsScreenWithGql = withGql(ExperimentationGroupsScreen)

export { ExperimentationGroupsScreenWithGql as ExperimentationGroupsScreen }

function removeExperimentationGroupFromHash() {
  window.location.hash = window.location.hash.replace(
    /\/#\/manage\/([^/]+)$/,
    ''
  )
}

function addExperimentationGroupToHash(experimentationGroupId: string) {
  window.location.hash = `${window.location.hash}/#/manage/${experimentationGroupId}`
}
